import { CalendarCheck, List, Info } from 'lucide-preact'
import type { HubSpotMappingConfig, HubSpotSyncModel } from '@/types/hubspot'

interface SyncModelStepProps {
    config: HubSpotMappingConfig
    marketingEventsAvailable: boolean
    onChange: (config: HubSpotMappingConfig) => void
}

export function SyncModelStep({ config, marketingEventsAvailable, onChange }: SyncModelStepProps) {
    const selectModel = (model: HubSpotSyncModel) => {
        onChange({ ...config, SyncModel: model })
    }

    return (
        <div className="space-y-6">
            <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Choose HubSpot Sync Model</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto">
                    Select how OrbitalX should represent campaign interactions within your HubSpot CRM. This determines the core structure of your integration.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Marketing Events Card */}
                <button
                    type="button"
                    onClick={() => selectModel('MarketingEvents')}
                    disabled={!marketingEventsAvailable}
                    className={`relative rounded-xl border-2 p-5 text-left transition-all ${
                        config.SyncModel === 'MarketingEvents'
                            ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    } ${!marketingEventsAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {marketingEventsAvailable && (
                        <span className="absolute -top-2.5 right-4 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-md bg-primary-500 text-white">
                            Recommended
                        </span>
                    )}
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
                            <CalendarCheck className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-slate-900 dark:text-white">Marketing Events</p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                Utilizes HubSpot's native Marketing Events object for a cleaner, more structured CRM representation.
                            </p>
                            <ul className="mt-3 space-y-1">
                                {['Best for supported HubSpot tiers', 'Keeps contact records uncluttered', 'Advanced reporting capabilities'].map(item => (
                                    <li key={item} className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400">
                                        <span className="w-1 h-1 rounded-full bg-current" /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {config.SyncModel === 'MarketingEvents' && (
                        <div className="absolute top-4 right-4 w-3 h-3 rounded-full bg-primary-500" />
                    )}
                </button>

                {/* Custom Fields Card */}
                <button
                    type="button"
                    onClick={() => selectModel('CustomFields')}
                    className={`relative rounded-xl border-2 p-5 text-left transition-all ${
                        config.SyncModel === 'CustomFields'
                            ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                >
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                            <List className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-slate-900 dark:text-white">Custom Fields</p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                Uses custom properties on contact records to track interactions. The legacy-compatible approach.
                            </p>
                            <ul className="mt-3 space-y-1">
                                {['Works for HubSpot Free tier', 'Simple property mapping', 'Easy to use in basic lists'].map(item => (
                                    <li key={item} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                        <span className="w-1 h-1 rounded-full bg-current" /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {config.SyncModel === 'CustomFields' && (
                        <div className="absolute top-4 right-4 w-3 h-3 rounded-full bg-primary-500" />
                    )}
                </button>
            </div>

            {!marketingEventsAvailable && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>Marketing Events requires Marketing Hub Professional or Enterprise. Your HubSpot subscription doesn't currently support this feature, so Custom Fields has been selected.</p>
                </div>
            )}

            {marketingEventsAvailable && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>We highly recommend Marketing Events if your HubSpot subscription supports it (Marketing Hub Professional or Enterprise). It provides a significantly better foundation for tracking campaign ROI.</p>
                </div>
            )}
        </div>
    )
}
